import React, { useMemo, useState } from 'react';
import { Paper, Typography, Stack, FormControl, Select, MenuItem, Button, Box, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import Chart from 'react-apexcharts';
import { exportCsvWithDate } from './csvUtil';
import { showTooltip, hideTooltip } from './tooltipManager';

const GRAPH_HEIGHT = 360;
const BIN_SIZE = 100;
const MAX_SCORE = 1500;

function makeBins(binSize = BIN_SIZE, maxScore = MAX_SCORE) {
  const labels = [];
  for (let start = 0; start < maxScore; start += binSize) {
    const end = Math.min(start + binSize - 1, maxScore - 1);
    labels.push(`${start}-${end}`);
  }
  labels.push(`${maxScore}`);
  return labels;
}

const EventDistributionChart = ({ eventsScores = {}, categoriesMap = {} }) => {
  const [valueType, setValueType] = useState('adjusted');
  const allEvents = useMemo(() => {
    const list = Object.keys(eventsScores || {}).map(eUid => {
      const ev = eventsScores[eUid];
      const number = Number(ev?.event?.number ?? Infinity);
      const label = ev?.event?.number ? `#${ev.event.number}${ev.event.name ? ' - ' + ev.event.name : ''}${ev.event.company ? ' - ' + ev.event.company : ''}` : (ev?.event?.name || eUid);
      return { uid: eUid, label, number };
    });
    list.sort((a, b) => {
      if (!Number.isNaN(a.number) && !Number.isNaN(b.number)) return a.number - b.number;
      return (a.label || '').localeCompare(b.label || '');
    });
    return list;
  }, [eventsScores]);

  const [selectedEvents, setSelectedEvents] = useState(() => {
    // default to the first 2 events in the dropdown
    return allEvents.slice(0, 2).map(e => e.uid);
  });

  const bins = useMemo(() => makeBins(BIN_SIZE, MAX_SCORE), []);

  const series = useMemo(() => {
    return (selectedEvents || []).map(eUid => {
      const ev = eventsScores[eUid] || { scores: [] };
      const counts = new Array(bins.length).fill(0);
      (ev.scores || []).forEach(s => {
        const val = valueType === 'adjusted' ? (s.finalScoreAssociated ?? s.finalScore) : (s.finalScore ?? 0);
        const n = Number(val || 0);
        if (isNaN(n)) return;
        if (n >= MAX_SCORE) { counts[bins.length-1]++; return; }
        const idx = Math.floor(n / BIN_SIZE);
        counts[idx]++;
      });
      const label = allEvents.find(e => e.uid === eUid)?.label || eUid;
      return { name: label, data: counts };
    });
  }, [selectedEvents, eventsScores, valueType, bins, allEvents]);

  const options = useMemo(() => ({
    chart: {
      id: 'event-distribution',
      toolbar: { show: true },
      animations: { enabled: false, dynamicAnimation: { enabled: false } },
      parentHeightOffset: 0,
      events: {
        dataPointMouseEnter: function(event, chartContext, { seriesIndex, dataPointIndex, w }) {
          try {
            const rect = (event && event.target && event.target.closest) ? (event.target.closest('path, rect') || event.target).getBoundingClientRect() : null;
            const x = rect ? (rect.left + rect.width / 2) : event.clientX;
            const y = rect ? rect.top : event.clientY;
            const bin = (w && w.config && w.config.xaxis && w.config.xaxis.categories && w.config.xaxis.categories[dataPointIndex]) || '';
            const val = (w && w.config && w.config.series && w.config.series[seriesIndex] && w.config.series[seriesIndex].data && w.config.series[seriesIndex].data[dataPointIndex]) || 0;
            const html = `<div style=\"pointer-events:none;padding:8px;font-size:13px;\"><strong>${bin}</strong><div style=\"margin-top:6px\">Count: <strong>${val}</strong></div></div>`;
            showTooltip({ html, x, y });
          } catch (e) {}
        },
        dataPointMouseLeave: function() { hideTooltip(0); },
        mouseLeave: function() { hideTooltip(0); }
      }
    },
    plotOptions: { bar: { horizontal: false, columnWidth: '60%' } },
    dataLabels: { enabled: false },
    xaxis: { categories: bins, labels: { rotate: -45 }, tooltip: { enabled: false }, crosshairs: { show: false } },
    yaxis: { title: { text: 'Count' } },
    grid: { padding: { top: 8, right: 8, bottom: 4, left: 4 } },
    tooltip: { enabled: false },
    states: { hover: { filter: { type: 'none' } } },
    fill: { opacity: 1, opacityOnHover: 1 }
  }), [bins]);

  const handleEventsChange = (e) => setSelectedEvents(e.target.value);
  const handleExport = () => {
    const headers = ['bin','eventLabel','count'];
    const rows = [];
    series.forEach(s => s.data.forEach((count, i) => rows.push({ bin: bins[i], eventLabel: s.name, count })));
    exportCsvWithDate('event-distribution', headers, rows);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Event Score Distributions (0–1500, buckets of 100)</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <Select multiple displayEmpty value={selectedEvents} onChange={handleEventsChange} input={<OutlinedInput />} renderValue={(selected) => selected.length ? `${selected.length} event(s)` : 'Select events'}>
              {allEvents.map(ev => (
                <MenuItem key={ev.uid} value={ev.uid}>
                  <Checkbox checked={selectedEvents.indexOf(ev.uid) > -1} />
                  <ListItemText primary={ev.label || ev.uid} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select value={valueType} onChange={(e)=>setValueType(e.target.value)}>
              <MenuItem value="adjusted">Adjusted</MenuItem>
              <MenuItem value="original">Original</MenuItem>
            </Select>
          </FormControl>

          <Button size="small" variant="outlined" onClick={handleExport}>Export CSV</Button>
        </Stack>
      </Stack>

      <Box sx={{ position: 'relative', height: GRAPH_HEIGHT, mt: 2 }}>
        <Chart type="bar" series={series} options={options} height={GRAPH_HEIGHT} />
      </Box>
    </Paper>
  );
};

export default EventDistributionChart;
